import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Box, Button, Checkbox, toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { CriteriaKeys } from 'components/CSAT/types'
import { usePutPaymentCaseMutation } from 'hooks/mutations/usePutPaymentCaseMutation'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { PaymentCaseUpdate } from 'types/payments'
import useCSATSurvey from '../hooks/useCSATSurvey'

interface CaseCompleteCheckboxProps {
  isCaseCompleted?: boolean
  caseId: string
}

export const CaseCompleteCheckbox = ({ isCaseCompleted, caseId }: CaseCompleteCheckboxProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isCompleted, setIsCompleted] = useState(false)
  const permissions = useMappedRoles().paymentSupport
  const canEdit = permissions?.canEdit
  const { mutate, isPending } = usePutPaymentCaseMutation()
  const { enableCsatSurveyCasePortal, getIsCriteriaCompleted, completeCSATCriteria } =
    useCSATSurvey()
  const isCaseCriteriaCompleted = getIsCriteriaCompleted(CriteriaKeys.IS_CASE_COMPLETED)

  useEffect(() => {
    setIsCompleted(isCaseCompleted)
  }, [isCaseCompleted])

  const handleOnChange = async value => {
    setIsCompleted(currentValue => !currentValue)
    const caseCompletedPayload: PaymentCaseUpdate = {
      customer: { customerCaseCompleted: value },
      caseId,
    }

    mutate(caseCompletedPayload, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: reactQueryKeys.paymentSupportCases() })

        if (value) {
          toast({
            header: t('paymentSupport.successCompleteMessage'),
            variant: 'success',
          })
          !isCaseCriteriaCompleted && enableCsatSurveyCasePortal && handleCSATSurveyUpdate()
        }
      },
      onError: error => {
        setIsCompleted(isCaseCompleted)
        toast({ header: t('paymentSupport.errorCompleteMessage'), variant: 'error' })
        logSentryError('Error: could not update payment case completed', error)
      },
    })
  }

  const handleCSATSurveyUpdate = () => {
    completeCSATCriteria(CriteriaKeys.IS_CASE_COMPLETED)
  }

  return (
    <>
      {isPending ? (
        <Box flex align="center" gapX={4}>
          <Button variant="ghost" loading={true} noPadding>
            {t('paymentSupport.markAsComplete')}
          </Button>
        </Box>
      ) : (
        <Checkbox
          id="case-complete-checkbox"
          name="case-complete-checkbox"
          testId="case-complete-checkbox"
          label={t('paymentSupport.markAsComplete')}
          disabled={!canEdit || isPending}
          checked={isCompleted}
          onCheckedChange={handleOnChange}
        />
      )}
    </>
  )
}
